import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Gavel, ListChecks, Megaphone, ShoppingBag } from 'lucide-react';
import { useNotifications } from '../context/NotificationsContext';
import type { AppNotification } from '../types';
import type { ViewKey } from './AppShell';
import { isNative } from '../native';
import { formatTime } from '../utils';

const validViews = new Set<ViewKey>(['dashboard', 'center', 'commissions', 'market', 'auctions', 'notices', 'members', 'settings', 'redeem', 'audit', 'profile']);

function viewFromLink(link: string): ViewKey | null {
  const target = link.replace(/^#\/?/, '').split('?')[0] as ViewKey;
  return validViews.has(target) ? target : null;
}

function NotificationIcon({ type }: { type: string }) {
  if (type.startsWith('commission')) return <ListChecks size={18} />;
  if (type.startsWith('market')) return <ShoppingBag size={18} />;
  if (type.startsWith('auction')) return <Gavel size={18} />;
  if (type.startsWith('notice')) return <Megaphone size={18} />;
  return <Bell size={18} />;
}

export function NotificationBell({ onNavigate }: { onNavigate: (view: ViewKey) => void }) {
  const { items, unreadCount, loading, permission, refresh, markRead, markAllRead, requestPermission } = useNotifications();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 68, right: 10 });
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({ top: rect.bottom + 10, right: Math.max(8, window.innerWidth - rect.right) });
  }, []);

  useEffect(() => {
    if (!open) return;
    void refresh();
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!wrapRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, refresh]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updatePosition]);

  const openNotification = async (notification: AppNotification) => {
    if (!notification.readAt) void markRead(notification.id);
    setOpen(false);
    const target = viewFromLink(notification.link);
    if (target) onNavigate(target);
  };

  const badge = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="notification-bell-wrap" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={'notification-bell' + (open ? ' open' : '')}
        aria-label="通知中心"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <Bell size={19} />
        {unreadCount > 0 && <span className="notification-badge">{badge}</span>}
      </button>
      {open && createPortal(
        <section
          ref={panelRef}
          className="notification-panel portal"
          aria-label="通知中心"
          style={{ '--notification-top': `${position.top}px`, '--notification-right': `${position.right}px` } as CSSProperties}
        >
          <header className="notification-panel-head">
            <div><strong>通知中心</strong><small>{unreadCount > 0 ? `${unreadCount} 条未读` : '暂无未读'}</small></div>
            {unreadCount > 0 && <button type="button" onClick={() => void markAllRead()}>全部已读</button>}
          </header>
          {isNative && permission !== 'granted' && (
            <div className="notification-permission">
              <span>系统通知未开启，可能收不到锁屏提醒</span>
              <button type="button" onClick={() => void requestPermission()}>开启</button>
            </div>
          )}
          <div className="notification-list">
            {loading && items.length === 0 ? (
              <div className="notification-empty">正在加载通知…</div>
            ) : items.length === 0 ? (
              <div className="notification-empty"><Bell size={26} /><span>还没有通知</span><small>新委托、公告、拍卖和交易提醒会出现在这里</small></div>
            ) : items.map(notification => (
              <button
                type="button"
                key={notification.id}
                className={'notification-item' + (notification.readAt ? '' : ' unread')}
                onClick={() => void openNotification(notification)}
              >
                <span className="notification-icon"><NotificationIcon type={notification.type} /></span>
                <span className="notification-copy">
                  <strong>{notification.title}</strong>
                  {notification.body && <p>{notification.body}</p>}
                  <time>{formatTime(notification.createdAt)}</time>
                </span>
                {!notification.readAt && <i className="notification-unread-dot" />}
              </button>
            ))}
          </div>
        </section>,
        document.body
      )}
    </div>
  );
}
